import React, { useEffect, useState } from "react";
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import "./EquipmentRegistrationModal.css";
import LabEquipmentApi from "./utils/labEquipmentApi";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { LabEquipmentManufacturer, LabEquipmentRobot } from "./models";

type EquipmentRegistrationModalProps = {
    isOpen: boolean;
    testGroup: "NAT" | "Serology";
    toggle: () => void;
    onSuccessfulRegistration: () => void;
}

const EquipmentRegistrationModal = (props: EquipmentRegistrationModalProps) => {
    const { isOpen, testGroup, toggle, onSuccessfulRegistration } = props;
    const [customEntry, setCustomEntry] = useState(false);
    const [confirmationMessage, setConfirmationMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [manufacturers, setManufacturers] = useState<LabEquipmentManufacturer[]>([]);
    const [robots, setRobots] = useState<LabEquipmentRobot[]>([]);
    const [equipmentData, setEquipmentData] = useState({
        manufacturer: "",
        instrument: "",
        deviceId: "",
        blocked: false,
        email: "",
        description: ""
    });

    useEffect(() => {
        if (isOpen) {
            const fetchManufacturers = async () => {
                try {
                    const response = await LabEquipmentApi.getManufacturerNames();
                    setManufacturers(response ? response.filter((r: LabEquipmentManufacturer) => r.methodTestGroup === testGroup): []);
                } catch (error) {
                    console.error("Failed to fetch manufacturers", error);
                }
            };
            fetchManufacturers();
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && equipmentData.manufacturer) {
            const fetchRobots = async () => {
                try {
                    const response = await LabEquipmentApi.getRobotNames(equipmentData.manufacturer);
                    setRobots(response ? response.filter((r: LabEquipmentManufacturer) => r.methodTestGroup === testGroup): []);
                } catch (error) {
                    console.error("Failed to fetch robot names", error);
                }
            };
            fetchRobots();
        }
    }, [isOpen, equipmentData.manufacturer]);

    const handleChange = (e: any) => {
        const { name, value, type, checked } = e.target;
        setEquipmentData(prevState => ({
            ...prevState,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    const submitEquipment = async () => {
        try {
            await LabEquipmentApi.saveNewEquipment({
                manufacturerName: equipmentData.manufacturer,
                robotName: equipmentData.instrument,
                labInstrument: equipmentData.deviceId
            });
            setConfirmationMessage('Equipment saved successfully!');
            setErrorMessage(null);
            onSuccessfulRegistration();
        } catch (error) {
            console.error(error);
            setErrorMessage('Request failed, please try again');
            setConfirmationMessage(null);
        }
    };

    const submitCustomEquipment = async () => {
        try {
            await LabEquipmentApi.saveNewCustomEquipment({
                manufacturer: equipmentData.manufacturer,
                instrument: equipmentData.instrument,
                deviceId: equipmentData.deviceId,
                email: equipmentData.email,
                description: equipmentData.description
            });
            setConfirmationMessage('Your notification has been sent!');
            setErrorMessage(null);
        } catch (error) {
            console.error(error);
            setErrorMessage('Request failed, please try again');
            setConfirmationMessage(null);
        }
    };



    return (
        <Modal isOpen={isOpen} toggle={toggle}>
            <ModalHeader toggle={toggle}>Add equipment</ModalHeader>
            <ModalBody>
                {!!errorMessage ? (
                    <div className='error-wrapper'>
                        <span className="error-icon">
                            <FaTimesCircle />
                        </span>
                        <p className="error-text">{errorMessage}</p>
                    </div>
                ) : (!!confirmationMessage ? (<div className="confirmation-wrapper">
                    <span className="confirmation-icon">
                        <FaCheckCircle />
                    </span>
                    <p className="confirmation-text">{confirmationMessage}</p>
                </div>) : (
                    !customEntry ? (
                        <>
                            <Label for="manufacturer">Manufacturer Name</Label>
                            <Input className="equipment-data-field" type="select" name="manufacturer" value={equipmentData.manufacturer} onChange={handleChange}>
                                <option value="" disabled>Select manufacturer</option>
                                {manufacturers?.map((manufacturer, index) => (
                                    <option key={index} value={manufacturer.robotManufacturerName}>{manufacturer.robotManufacturerName}</option>
                                ))}
                            </Input>
                            <Label for="robotName">Robot Name</Label>
                            <Input className="equipment-data-field" type="select" name="instrument" value={equipmentData.instrument} onChange={handleChange} disabled={!robots.length}>
                                <option value="" disabled>Select robot</option>
                                {robots?.map((robot, index) => (
                                    <option key={index} value={robot.robotName}>{robot.robotName}</option>
                                ))}
                            </Input>
                            <Label for="instrumentCode">New Lab Instrument Code</Label>
                            <Input className="equipment-data-field" type="text" name="deviceId" value={equipmentData.deviceId} onChange={handleChange} placeholder="Your reference" />
                            {/* <FormGroup check>
                                <Label check>
                                    <Input className="equipment-data-field" type="checkbox" name="blocked" checked={equipmentData.blocked} onChange={handleChange} /> Blocked
                                </Label>
                            </FormGroup> */}
                            <span className="custom-link" onClick={() => setCustomEntry(true)}>My equipment is not in this list</span>
                        </>
                    ) : (
                        <>
                            <p className="instructions-wrapper">Please send us the details of your equipment and we'll add it for you.</p>
                            <Input className="equipment-data-field" type="text" name="manufacturer" value={equipmentData.manufacturer} onChange={handleChange} placeholder="Manufacturer Name" />
                            <Input className="equipment-data-field" type="text" name="instrument" value={equipmentData.instrument} onChange={handleChange} placeholder="Robot name" />
                            <Input className="equipment-data-field" type="text" name="deviceId" value={equipmentData.deviceId} onChange={handleChange} placeholder="New Lab Instrumentcode" />
                            <Input className="equipment-data-field" type="email" name="email" value={equipmentData.email} onChange={handleChange} placeholder="Enter email" />
                            <Input className="equipment-data-field" type="textarea" name="description" value={equipmentData.description} onChange={handleChange} placeholder="Additional notes." />
                        </>
                    )
                ))}
            </ModalBody>
            <ModalFooter>
                {!!confirmationMessage || !!errorMessage ? (
                    <Button color="primary" onClick={toggle}>Close</Button>
                ) : (
                    !customEntry ? (
                        <>
                            <Button color="secondary" onClick={toggle}>Cancel</Button>
                            <Button color="primary" onClick={submitEquipment}>Save equipment</Button>
                        </>
                    ) : (
                        <>
                            <Button color="secondary" onClick={() => setCustomEntry(false)}>Back</Button>
                            <Button color="primary" onClick={submitCustomEquipment}>Submit request</Button>
                        </>
                    )
                )}
            </ModalFooter>
        </Modal>
    );
};

export default EquipmentRegistrationModal;